/**
 * Unterminated Liquid, caught before the save rather than after it.
 *
 * `checkForInvalidVariables` only compares the names inside `{{ … }}` against the
 * list a surface offers, so it needs a closing `}}` to find anything at all. A
 * half-typed or half-pasted `{{candidate.name` is not an unknown variable, it is
 * broken syntax — it slips past that check, reaches Rails, and comes back as a
 * refused save (CORE-5404).
 *
 * Deliberately narrow: only an opener with no closer, which is what Liquid's own
 * "was not properly terminated" covers and what a human actually produces. A
 * false positive here would block a save that would have worked, so anything
 * subtler is left to the server, which parses properly and is still the gate.
 */

const DELIMITERS = [
  { open: '{{', close: '}}', what: 'variable' },
  { open: '{%', close: '%}', what: 'tag' },
] as const;

/** Enough of the offender to find it by eye, without quoting a paragraph back. */
const EXCERPT_LENGTH = 24;

const excerpt = (from: string) => {
  const line = from.split(/[\r\n]/)[0];
  return line.length > EXCERPT_LENGTH
    ? `${line.slice(0, EXCERPT_LENGTH).trimEnd()}…`
    : line.trimEnd();
};

export interface LiquidSyntaxProblem {
  /** Which field it was found in, named the way the server names it. */
  field: string;
  /** What is wrong, in the reader's terms rather than Liquid's. */
  reason: string;
}

/** The first unterminated opener in one string, or undefined. */
export const findUnterminated = (
  content: string
): { what: string; snippet: string } | undefined => {
  let index = 0;

  while (index < content.length) {
    // Whichever opens first from here — a `{%` inside an unclosed `{{` is the
    // `{{`'s problem, not its own.
    const next = DELIMITERS.map((d) => ({
      d,
      at: content.indexOf(d.open, index),
    }))
      .filter(({ at }) => at !== -1)
      .sort((a, b) => a.at - b.at)[0];

    if (!next) return undefined;

    const closesAt = content.indexOf(
      next.d.close,
      next.at + next.d.open.length
    );
    if (closesAt === -1) {
      return { what: next.d.what, snippet: excerpt(content.slice(next.at)) };
    }

    index = closesAt + next.d.close.length;
  }

  return undefined;
};

/**
 * The first field with unterminated Liquid. Subject before body, and one at a
 * time — the same order and the same restraint the server shows, so the two
 * never disagree about which problem to name first.
 */
export const findLiquidSyntaxProblem = (fields: {
  subject?: string;
  body?: string;
}): LiquidSyntaxProblem | undefined => {
  const ordered: [string, string | undefined][] = [
    ['subject', fields.subject],
    ['body', fields.body],
  ];

  for (const [field, content] of ordered) {
    if (!content) continue;

    const found = findUnterminated(content);
    if (found)
      return {
        field,
        reason: `${found.what} "${found.snippet}" is missing its closing ${
          found.what === 'variable' ? '}}' : '%}'
        }`,
      };
  }

  return undefined;
};

/**
 * An output tag with nothing in it — `{{}}`, `{{ }}`, `{{- -}}`.
 *
 * Liquid parses one happily and renders it as an empty string, so it is not a syntax
 * error and #findUnterminated cannot see it — it reached the recipient as a hole in
 * the sentence.
 *
 * "Nothing" is wider than the space bar, and all three definitions of it have to
 * agree or a sender gets a contradiction:
 *
 * | source                                                  | what it is for            |
 * | ------------------------------------------------------- | ------------------------- |
 * | `AtsCommTemplate::EMPTY_LIQUID_TAG` (core, CORE-5463)    | refuses the send, 422     |
 * | `htmlHasEmptyVariablePlaceholders` (`@kula-ai/components`) | the red field, inline   |
 * | this                                                    | refuses before either     |
 *
 * It was `/\{\{[-\s]*\}\}/`, which is narrower than both, and the gap had a user
 * cost: paste a non-breaking space into a BODY and it arrives as the `&nbsp;` ENTITY
 * (a subject is plain text, so the same paste arrives there as U+00A0) — so the
 * component drew the field red, this let the send go, and core answered 422. Three
 * forms were missing: `&nbsp;` / `&#160;` / `&#xa0;`, and U+200B.
 *
 * U+00A0 and U+FEFF need no listing — JS `\s` already covers both, verified rather
 * than assumed. The equivalence to the component's own predicate is pinned by spec,
 * which is a stronger guard than the two regexes looking alike.
 *
 * NOT `checkForInvalidVariables`, which is the other thing entirely: that flags any
 * name absent from the list a surface offers, and that list is not the product's
 * variable surface — validating on it server-side blocked consent-email setup and
 * new-account seeding before it was reverted (CORE-5463). It stays a warning. This
 * is the narrow half the server actually refuses.
 */
const EMPTY_VARIABLE = /\{\{(?:[-\s\u200B]|&nbsp;|&#160;|&#xa0;)*\}\}/i;

export const findEmptyVariable = (fields: {
  subject?: string;
  body?: string;
}): LiquidSyntaxProblem | undefined => {
  const ordered: [string, string | undefined][] = [
    ['subject', fields.subject],
    ['body', fields.body],
  ];

  for (const [field, content] of ordered) {
    if (content && EMPTY_VARIABLE.test(content)) {
      return { field, reason: 'a variable has no name in it' };
    }
  }

  return undefined;
};

/**
 * Everything that makes the server refuse this content, in the order the banner
 * names them.
 *
 * Broken syntax first, matching #getInvalidVariableAlertConfig: one stops the save
 * outright, the other is about what the recipient will not see, and naming the
 * lesser problem while the greater one blocks the button is how a sender ends up
 * fixing the wrong thing.
 */
export const findBlockingVariableProblem = (fields: {
  subject?: string;
  body?: string;
}): LiquidSyntaxProblem | undefined =>
  findLiquidSyntaxProblem(fields) ?? findEmptyVariable(fields);
